import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  appendDeliveryScan,
  cancelDelivery,
  completeDelivery,
  createDelivery,
  DeliveryCreatePayload,
  DeliveryRecord,
  DeliveryScanRecord,
  DeliveryStatus,
  getDelivery,
  listDeliveries,
  listDeliveryScans,
  ManifestItem,
  parseTokenClaims,
  startDelivery,
} from "../../api";
import { Icon } from "../../icons";

export function DeliveryPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const isOperator = claims?.role === "ADMIN" || claims?.role === "OPERATOR";

  const [list, setList] = useState<DeliveryRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<DeliveryRecord | null>(null);
  const [scans, setScans] = useState<DeliveryScanRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<DeliveryStatus | "">("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setError(null);
    try {
      const rows = await listDeliveries({ status: statusFilter || undefined });
      setList(rows);
      if (!selectedId && rows.length > 0) setSelectedId(rows[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function refreshDetail() {
    if (!selectedId) return;
    try {
      const [d, s] = await Promise.all([
        getDelivery(selectedId),
        listDeliveryScans(selectedId),
      ]);
      setSelected(d);
      setScans(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    setSelected(null);
    setScans([]);
    if (selectedId) void refreshDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="boxes" /> {t("delivery.title")}
          </h2>
          <p className="muted">{t("delivery.intro")}</p>
        </div>
        <div>
          <label className="muted small" style={{ marginRight: "0.5rem" }}>
            {t("delivery.statusFilter")}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DeliveryStatus | "")}
          >
            <option value="">{t("delivery.all")}</option>
            <option value="pending">{t("delivery.pendingOpt")}</option>
            <option value="in_progress">{t("delivery.inProgressOpt")}</option>
            <option value="completed">{t("delivery.completedOpt")}</option>
            <option value="cancelled">{t("delivery.cancelledOpt")}</option>
          </select>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {info && <p className="info">{info}</p>}

      {isOperator && (
        <details className="delivery-create">
          <summary>
            <Icon name="boxes" /> {t("delivery.newDelivery")}
          </summary>
          <NewDeliveryForm
            onCreated={async (d) => {
              setInfo(t("delivery.createdMsg", { id: d.id }));
              setSelectedId(d.id);
              await refresh();
            }}
            onError={(m) => setError(m)}
          />
        </details>
      )}

      <div className="delivery-layout">
        <div className="delivery-list-col">
          <h3>{t("delivery.listTitle")}</h3>
          {loading ? (
            <p className="muted">{t("common:actions.loading")}</p>
          ) : list.length === 0 ? (
            <div className="empty-card">
              <Icon name="boxes" /> {t("delivery.emptyFilter")}
            </div>
          ) : (
            <ul className="delivery-list">
              {list.map((d) => (
                <li
                  key={d.id}
                  className={d.id === selectedId ? "active" : ""}
                  onClick={() => setSelectedId(d.id)}
                >
                  <div className="delivery-list-head">
                    <strong>{d.destination_label}</strong>
                    <span className={`status-pill status-${d.status}-pill`}>
                      {t(`delivery.status.${d.status}`)}
                    </span>
                  </div>
                  <div className="delivery-list-meta">
                    {d.vehicle_id ?? "—"} · {t("delivery.boxes", { ok: d.ok_count, total: d.manifest_count, pct: d.progress_pct })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="delivery-detail-col">
          {selected ? (
            <DeliveryDetail
              delivery={selected}
              scans={scans}
              canControl={isOperator || claims?.role === "DRIVER"}
              onRefresh={async () => {
                await refreshDetail();
                await refresh();
              }}
              onError={(m) => setError(m)}
              onInfo={(m) => setInfo(m)}
            />
          ) : (
            <p className="muted">{t("delivery.selectPrompt")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function DeliveryDetail({
  delivery,
  scans,
  canControl,
  onRefresh,
  onError,
  onInfo,
}: {
  delivery: DeliveryRecord;
  scans: DeliveryScanRecord[];
  canControl: boolean;
  onRefresh: () => Promise<void>;
  onError: (m: string) => void;
  onInfo: (m: string) => void;
}) {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const [barcode, setBarcode] = useState("");
  const [busy, setBusy] = useState(false);

  const manifestRemaining = useMemo(() => {
    const need = new Map<string, number>();
    for (const item of delivery.manifest) {
      need.set(item.barcode, (need.get(item.barcode) ?? 0) + item.qty);
    }
    for (const s of scans) {
      if (s.status === "ok") {
        need.set(s.barcode, (need.get(s.barcode) ?? 0) - 1);
      }
    }
    return Array.from(need.entries()).filter(([, n]) => n > 0);
  }, [delivery, scans]);

  async function doScan() {
    if (!barcode.trim()) return;
    setBusy(true);
    try {
      const result = await appendDeliveryScan(delivery.id, barcode.trim());
      const resultLabel = t(`delivery.scan.${result.status}`, { defaultValue: result.status });
      onInfo(t("delivery.detail.scanMsg", { barcode: barcode.trim(), result: resultLabel }));
      setBarcode("");
      await onRefresh();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doStart() {
    setBusy(true);
    try {
      await startDelivery(delivery.id);
      await onRefresh();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doComplete() {
    setBusy(true);
    try {
      await completeDelivery(delivery.id);
      onInfo(t("delivery.detail.completedMsg"));
      await onRefresh();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doCancel() {
    if (!confirm(t("delivery.detail.cancelConfirm"))) return;
    setBusy(true);
    try {
      await cancelDelivery(delivery.id);
      onInfo(t("delivery.detail.cancelledMsg"));
      await onRefresh();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const active = delivery.status === "pending" || delivery.status === "in_progress";

  return (
    <div>
      <h3>
        {delivery.destination_label}{" "}
        <span className={`status-pill status-${delivery.status}-pill`}>
          {t(`delivery.status.${delivery.status}`)}
        </span>
      </h3>
      <p className="muted small">
        {t("delivery.detail.idLine", {
          id: delivery.id,
          vehicle: delivery.vehicle_id ?? "—",
          driver: delivery.driver_id ?? "—",
          order: delivery.order_ref ?? "—",
        })}
      </p>

      <div className="progress-wrap">
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${delivery.progress_pct}%` }}
          />
        </div>
        <div className="progress-label">
          {t("delivery.detail.progressLabel", {
            ok: delivery.ok_count,
            total: delivery.manifest_count,
            pct: delivery.progress_pct,
            scans: delivery.scanned_count,
          })}
        </div>
      </div>

      {canControl && active && (
        <div className="delivery-actions">
          <div className="row-form">
            <input
              type="text"
              placeholder={t("delivery.detail.barcodePlaceholder")}
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScan()}
            />
            <button type="button" onClick={doScan} disabled={busy}>
              {t("delivery.detail.scanBtn")}
            </button>
          </div>
          <div className="row-form">
            {delivery.status === "pending" && (
              <button type="button" onClick={doStart} disabled={busy}>
                {t("delivery.detail.startBtn")}
              </button>
            )}
            <button type="button" className="ghost" onClick={doComplete} disabled={busy}>
              {t("delivery.detail.completeBtn")}
            </button>
            <button type="button" className="danger" onClick={doCancel} disabled={busy}>
              {t("delivery.detail.cancelBtn")}
            </button>
          </div>
        </div>
      )}

      <h4 style={{ marginTop: "1rem" }}>{t("delivery.detail.manifest")}</h4>
      <table>
        <thead>
          <tr>
            <th>{t("delivery.detail.th.barcode")}</th>
            <th>{t("delivery.detail.th.label")}</th>
            <th>{t("delivery.detail.th.qty")}</th>
            <th>{t("delivery.detail.th.remaining")}</th>
          </tr>
        </thead>
        <tbody>
          {delivery.manifest.map((m) => {
            const remaining = manifestRemaining.find(([code]) => code === m.barcode);
            return (
              <tr key={m.barcode}>
                <td>
                  <code>{m.barcode}</code>
                </td>
                <td>{m.label ?? "—"}</td>
                <td>{m.qty}</td>
                <td>{remaining ? remaining[1] : 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h4 style={{ marginTop: "1rem" }}>{t("delivery.detail.scanHistory")}</h4>
      {scans.length === 0 ? (
        <p className="muted small">{t("delivery.detail.noScans")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("delivery.detail.th.time")}</th>
              <th>{t("delivery.detail.th.barcode")}</th>
              <th>{t("delivery.detail.th.result")}</th>
            </tr>
          </thead>
          <tbody>
            {scans.map((s) => (
              <tr key={s.id} className={`scan-${s.status}`}>
                <td>{new Date(s.scanned_at).toLocaleTimeString(i18n.resolvedLanguage)}</td>
                <td>
                  <code>{s.barcode}</code>
                </td>
                <td>
                  <span className={`scan-pill scan-${s.status}`}>
                    {t(`delivery.scan.${s.status}`, { defaultValue: s.status })}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewDeliveryForm({
  onCreated,
  onError,
}: {
  onCreated: (d: DeliveryRecord) => void;
  onError: (m: string) => void;
}) {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const firmId = claims?.firm_id ?? "";
  const [form, setForm] = useState<DeliveryCreatePayload>({
    firm_id: firmId,
    destination_label: "",
    manifest: [],
  });
  const [draftRow, setDraftRow] = useState<ManifestItem>({ barcode: "", label: "", qty: 1 });
  const [busy, setBusy] = useState(false);

  function addRow() {
    if (!draftRow.barcode.trim()) return;
    setForm((f) => ({ ...f, manifest: [...f.manifest, { ...draftRow }] }));
    setDraftRow({ barcode: "", label: "", qty: 1 });
  }

  function removeRow(idx: number) {
    setForm((f) => ({
      ...f,
      manifest: f.manifest.filter((_, i) => i !== idx),
    }));
  }

  async function submit() {
    if (!form.destination_label.trim() || form.manifest.length === 0) {
      onError(t("delivery.form.needFields"));
      return;
    }
    setBusy(true);
    try {
      const created = await createDelivery({
        ...form,
        firm_id: firmId,
      });
      setForm({ firm_id: firmId, destination_label: "", manifest: [] });
      onCreated(created);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="delivery-form">
      <div className="row-form grid">
        <label>
          {t("delivery.form.destination")}
          <input
            type="text"
            value={form.destination_label}
            onChange={(e) =>
              setForm((f) => ({ ...f, destination_label: e.target.value }))
            }
            required
          />
        </label>
        <label>
          {t("delivery.form.vehicle")}
          <input
            type="text"
            value={form.vehicle_id ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, vehicle_id: e.target.value || null }))
            }
          />
        </label>
        <label>
          {t("delivery.form.driver")}
          <input
            type="text"
            value={form.driver_id ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, driver_id: e.target.value || null }))
            }
          />
        </label>
        <label>
          {t("delivery.form.orderRef")}
          <input
            type="text"
            value={form.order_ref ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, order_ref: e.target.value || null }))
            }
          />
        </label>
      </div>

      <h4>{t("delivery.form.manifest")}</h4>
      <div className="row-form">
        <input
          type="text"
          placeholder={t("delivery.form.barcode")}
          value={draftRow.barcode}
          onChange={(e) => setDraftRow({ ...draftRow, barcode: e.target.value })}
        />
        <input
          type="text"
          placeholder={t("delivery.form.label")}
          value={draftRow.label ?? ""}
          onChange={(e) => setDraftRow({ ...draftRow, label: e.target.value })}
        />
        <input
          type="number"
          min="1"
          value={draftRow.qty}
          onChange={(e) => setDraftRow({ ...draftRow, qty: parseInt(e.target.value, 10) || 1 })}
        />
        <button type="button" onClick={addRow}>
          {t("delivery.form.add")}
        </button>
      </div>
      {form.manifest.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>{t("delivery.form.th.barcode")}</th>
              <th>{t("delivery.form.th.label")}</th>
              <th>{t("delivery.form.th.qty")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {form.manifest.map((m, i) => (
              <tr key={i}>
                <td>
                  <code>{m.barcode}</code>
                </td>
                <td>{m.label ?? "—"}</td>
                <td>{m.qty}</td>
                <td>
                  <button type="button" className="danger" onClick={() => removeRow(i)}>
                    {t("delivery.form.delete")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button type="button" onClick={submit} disabled={busy} style={{ marginTop: "0.5rem" }}>
        {busy ? t("delivery.form.creating") : t("delivery.form.save")}
      </button>
    </div>
  );
}
